import { Button } from "@preview-kit/ui/components/button"
import { Input } from "@preview-kit/ui/components/input"
import { Separator } from "@preview-kit/ui/components/separator"
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@preview-kit/ui/components/sheet"
import "@preview-kit/ui/styles/globals.css"
import { Filter } from "lucide-react"

import { api } from "@/lib/api"
import { authClient } from "@/lib/auth-client"
import type { Comment } from "@/types/const.types"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import CommentItem from "./comment-item"

type Props = {
	commentsOpen: boolean
	setCommentsOpen: (open: boolean) => void
}

export default function InboxSheet({ commentsOpen, setCommentsOpen }: Props) {
	const [searchTerm, setSearchTerm] = useState("")
	const { data: session } = authClient.useSession()

	const comments = useQuery({
		queryKey: ["comments"],
		enabled: !!window && !!session?.user,
		queryFn: async () =>
			await api
				.get(
					`comments/hostname/${window.location.hostname}?pageUrl=${window.location.pathname}`,
				)
				.json<Comment[]>(),
	})
	return (
		<Sheet open={commentsOpen} onOpenChange={setCommentsOpen}>
			<SheetContent
				side="right"
				container={window.__PREVIEWKIT_PORTAL_CONTAINER__}
				className="w-[400px] gap-0 m-4 h-[calc(100vh-2rem)] rounded-lg border-neutral-200 border-1 outline-none"
			>
				<SheetHeader>
					<SheetTitle className="dark:text-white">Comments</SheetTitle>
					<SheetDescription className="text-[#666]">
						View and manage your comments
					</SheetDescription>
				</SheetHeader>

				<Separator className=" dark:bg-[#333]" />

				{/* Search and Filter - only show when user is authenticated */}
				{session?.user && (
					<>
						<div className=" space-y-3 px-2 my-3">
							<div className="flex gap-2">
								<Input
									placeholder="Search comments..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className="flex-1 text-black  "
								/>
								<Button variant="outline" size="icon" className="">
									<Filter className="w-4 h-4" />
								</Button>
							</div>
						</div>
						<Separator className=" dark:bg-[#333] m-0" />
					</>
				)}

				<div className=" px-2 overflow-y-scroll">
					{!session?.user ? (
						// Login banner when user is not authenticated
						<div className="flex flex-col items-center justify-center h-64 px-4 text-center">
							<div className="mb-4">
								<svg
									className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1}
										d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
									/>
								</svg>
							</div>
							<h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
								Sign in to view your inbox
							</h3>
							<p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-sm">
								You need to be logged in to view and manage your comments. Sign
								in to get started.
							</p>
							<a
								href={
									"http://localhost:3000/auth/login?next=" +
									encodeURIComponent(window.location.href)
								}
								className="w-full max-w-xs"
							>
								<Button
									onClick={() => {
										// You can implement your sign-in logic here
										// For now, this is a placeholder
										console.log("Sign in clicked")
									}}
									className="w-full max-w-xs"
								>
									Sign In
								</Button>
							</a>
						</div>
					) : (
						// Comments list when user is authenticated
						comments.data?.map((comment) => (
							<CommentItem
								key={comment.id}
								comment={comment}
								author={comment.user?.name || "Unknown User"}
								content={comment.content}
								timestamp={
									comment?.createdAt?.toString() || new Date().toISOString()
								}
								avatar={
									comment.user?.image ||
									"https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"
								}
							/>
						)) || null
					)}
				</div>
			</SheetContent>
		</Sheet>
	)
}
